import { Icon } from '@iconify/react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { z } from 'zod';

import type { BlogType, getBlogPost } from '@/services/strapi/blogs';
import { extractToc, processBlogContentHtml } from '@/utils/blogContent';
import { documentDateFormatter } from '@/utils/documentDateFormatter';
import { createBreadcrumbSchema } from '@/utils/schema-org';
import { createMetaTags } from '@/utils/seo';

import Chip from '../Chip';
import HtmlContent from '../HtmlContent';
import Stack from '../Landing/Stack';
import Text from '../Text';
import { blogTypesConfig } from './blogTypesConfig';
import ContactCta from './ContactCta';
import ReadOtherArticles from './ReadOtherArticles';
import ShareBlogVia from './ShareBlogVia';
import TableOfContents from './TableOfContents';

type PostData = Awaited<ReturnType<typeof getBlogPost>>['data'][number];

const READING_WORDS_PER_MINUTE = 150;

const BlogPost = ({
  postData,
  blogType,
}: {
  postData: PostData;
  blogType: BlogType;
}) => {
  const { query } = useRouter();
  const search = z.string().optional().parse(query.search);
  const slug = z
    .string()
    .optional()
    .parse(query.slug ?? postData.slug);

  const {
    createdAt,
    updatedAt,
    title,
    tags,
    bannerImage,
    content,
    seoDescription,
    seoTitle,
    standardImage,
  } = postData;
  const tag = tags[0]?.tag;

  const readingTime = Math.ceil(
    content.split(' ').length / READING_WORDS_PER_MINUTE
  );

  const {
    basePath,
    chipVariant,
    hasTableOfContents,
    hasContactCta,
    hasRelatedByTag,
  } = blogTypesConfig[blogType];

  const renderedContent = useMemo(
    () => (hasTableOfContents ? processBlogContentHtml(content) : content),
    [content, hasTableOfContents]
  );
  const tocItems = useMemo(
    () => (hasTableOfContents ? extractToc(renderedContent) : []),
    [renderedContent, hasTableOfContents]
  );

  const baseUrl = `https://mindpal.co${basePath}`;
  const canonicalUrl = slug ? `${baseUrl}/${slug}` : baseUrl;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': blogType === 'PRESSROOM' ? 'NewsArticle' : 'Article',
    headline: seoTitle,
    description: seoDescription,
    image: standardImage.url,
    datePublished: new Date(createdAt).toISOString(),
    dateModified: new Date(updatedAt).toISOString(),
    timeRequired: `PT${readingTime}M`,
    author: {
      '@type': 'Organization',
      name: 'MindPal',
      url: 'https://mindpal.co',
    },
    publisher: {
      '@type': 'Organization',
      name: 'MindPal',
      url: 'https://mindpal.co',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  const hasSideRail = hasTableOfContents || hasContactCta;

  return (
    <>
      <style jsx global>
        {`
          body {
            background: #fafafa;
          }
        `}
      </style>
      <Head>
        {createMetaTags({
          title: seoTitle,
          description: seoDescription,
          ogImage: standardImage.url,
        })}
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              createBreadcrumbSchema(slug ? `${basePath}/${slug}` : basePath)
            ),
          }}
        />
      </Head>
      <Stack>
        <div className="flex w-full flex-col items-center lg:flex-row lg:items-start">
          <div className="w-full flex-1">
            <Link href={{ pathname: basePath, query: { search } }}>
              <Text
                className="flex flex-row items-center gap-2 text-jb-neutral-800 transition-colors hover:text-jb-primary-500"
                variant="jb-body-medium"
              >
                <Icon icon="material-symbols-light:arrow-back" fontSize={16} />
                All posts
              </Text>
            </Link>
          </div>
          <div className="flex w-full max-w-xl flex-col gap-6">
            <Text
              className="text-end text-jb-neutral-800"
              variant="jb-body-medium"
            >
              {documentDateFormatter(createdAt)}
            </Text>
            <Text variant="h3" as="h1" className="font-semibold">
              {title}
            </Text>
            {!!tag && (
              <Chip
                className="flex w-fit !gap-0 font-semibold"
                size="sm-long"
                variant={chipVariant}
              >
                <span className="italic">#</span>
                {tag}
              </Chip>
            )}
            <div className="flex flex-col gap-2">
              <Text
                variant="jb-body-small"
                className="text-jb-neutral-800"
              >{`${readingTime} min read`}</Text>
              <Image
                alt={title}
                blurDataURL={bannerImage.placeholder ?? undefined}
                placeholder={bannerImage.placeholder ? 'blur' : 'empty'}
                width={bannerImage.width}
                height={bannerImage.height}
                src={bannerImage.url}
                className="aspect-[5/2] w-full rounded-xl object-cover object-center"
              />
            </div>
            {!!tocItems.length && (
              <TableOfContents items={tocItems} className="lg:hidden" />
            )}
            <HtmlContent
              content={renderedContent}
              variant="cmsContent"
              className={`!gap-0 !text-jb-neutral-1000 ${
                hasTableOfContents
                  ? '[&_h2]:scroll-mt-1 [&_h3]:scroll-mt-1 [&_h4]:scroll-mt-1'
                  : ''
              }`}
            />
            <ShareBlogVia />
          </div>
          {hasSideRail ? (
            <aside className="flex w-full flex-1 flex-col gap-6 pb-4 pt-10 lg:sticky lg:top-24 lg:max-w-xs lg:pb-10 lg:pl-8 lg:pt-0">
              {!!tocItems.length && (
                <TableOfContents items={tocItems} className="hidden lg:block" />
              )}
              {hasContactCta && <ContactCta />}
            </aside>
          ) : (
            <div className="flex-1" />
          )}
        </div>
        <ReadOtherArticles
          blogType={blogType}
          tagDocumentId={hasRelatedByTag ? tags[0]?.documentId : undefined}
        />
      </Stack>
    </>
  );
};

export default BlogPost;
